import { cn } from '../lib/cn';

export interface StepperProps {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  /**
   * `round` is the 26px passenger counter in the hero search widget; `square`
   * is the 32px seat counter in the publish flow.
   */
  shape?: 'round' | 'square';
  /** Accessible name — "Passengers", "Seats available". */
  label: string;
  className?: string;
}

/** The −/+ counter. Both shapes share behaviour and differ only in chrome. */
export function Stepper({
  value,
  onValueChange,
  min = 1,
  max = 4,
  shape = 'round',
  label,
  className,
}: StepperProps) {
  const button = cn(
    'flex cursor-pointer items-center justify-center border border-line bg-surface text-muted',
    'leading-none transition-colors duration-fast ease-standard hover:bg-hover',
    'disabled:cursor-not-allowed disabled:opacity-40',
    shape === 'round'
      ? 'h-[26px] w-[26px] rounded-full text-lead'
      : 'h-8 w-8 rounded-sm text-title-sm',
  );

  return (
    <div
      className={cn('flex items-center', shape === 'round' ? 'gap-2.5' : 'gap-3.5', className)}
      role="group"
      aria-label={label}
    >
      <button
        type="button"
        className={button}
        onClick={() => onValueChange(Math.max(min, value - 1))}
        disabled={value <= min}
        aria-label={`Decrease ${label.toLowerCase()}`}
      >
        −
      </button>
      <span
        aria-live="polite"
        className={cn(
          'text-center font-bold tabular',
          shape === 'round' ? 'min-w-[14px] text-base' : 'min-w-[18px] font-display text-title-lg',
        )}
      >
        {value}
      </span>
      <button
        type="button"
        className={button}
        onClick={() => onValueChange(Math.min(max, value + 1))}
        disabled={value >= max}
        aria-label={`Increase ${label.toLowerCase()}`}
      >
        +
      </button>
    </div>
  );
}

export interface RangeSliderProps {
  value: number;
  onValueChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  label: string;
  /** Formatted current value shown beside the label — "$40". */
  valueLabel?: string;
  className?: string;
}

/** The max-price slider in the search filters. */
export function RangeSlider({
  value,
  onValueChange,
  min,
  max,
  step = 1,
  label,
  valueLabel,
  className,
}: RangeSliderProps) {
  return (
    <div className={cn('flex flex-col', className)}>
      <div className="mb-2 flex justify-between">
        <span className="text-label font-semibold">{label}</span>
        {valueLabel ? (
          <span className="text-label font-bold text-ngahere">{valueLabel}</span>
        ) : null}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={(event) => onValueChange(Number(event.target.value))}
        className="w-full cursor-pointer accent-ngahere"
      />
    </div>
  );
}
